import { itemTypes, procurementTypes } from "./enums";
import type { ListColumnSpec } from "./listQuery";

// Column specs of the list RPCs (docs/design/column-filters.md). Keys, types
// and sortability mirror the c_spec inside each *_page function; a pgTAP test
// per list covers the database side, these drive the grid header, the URL
// and agent tools.

/** item_page — PX-MDT-110 (master data: every column sortable). */
export const itemListColumns: readonly ListColumnSpec[] = [
  { key: "code", type: "text", sortable: true },
  { key: "description", type: "text", sortable: true },
  { key: "type", type: "enum", sortable: true, values: itemTypes },
  { key: "procurement", type: "enum", sortable: true, values: procurementTypes },
  { key: "uom", type: "text", sortable: true },
  { key: "tracking", type: "enum", values: ["none", "lot", "serial"] },
  { key: "active", type: "bool" },
];

export const purchaseOrderStatuses = ["draft", "released", "partially_received", "received", "closed", "cancelled"] as const;

/** purchase_order_page — PX-PUR-110 (document). */
export const purchaseOrderListColumns: readonly ListColumnSpec[] = [
  { key: "number", type: "text", sortable: true },
  { key: "supplier", type: "text", sortable: true },
  { key: "status", type: "enum", sortable: true, values: purchaseOrderStatuses },
  { key: "date", type: "date", sortable: true },
  { key: "delivery", type: "date", sortable: true },
  { key: "amount", type: "number", sortable: true },
  { key: "currency", type: "enum", sortable: true },
  { key: "lines", type: "number", sortable: true },
];

export const salesOrderStatuses = ["draft", "released", "partially_shipped", "shipped", "closed", "cancelled"] as const;

/** sales_order_page — PX-SAL-110 (document). */
export const salesOrderListColumns: readonly ListColumnSpec[] = [
  { key: "number", type: "text", sortable: true },
  { key: "customer", type: "text", sortable: true },
  { key: "status", type: "enum", sortable: true, values: salesOrderStatuses },
  { key: "date", type: "date", sortable: true },
  { key: "requested", type: "date", sortable: true },
  { key: "reserved", type: "number", sortable: true },
  { key: "amount", type: "number", sortable: true },
  { key: "currency", type: "enum", sortable: true },
  { key: "lines", type: "number", sortable: true },
];

export const productionOrderStatuses = ["planned", "released", "in_progress", "completed", "closed", "cancelled"] as const;

/** production_order_page — PX-MFG-110 (document). */
export const productionOrderListColumns: readonly ListColumnSpec[] = [
  { key: "number", type: "text", sortable: true },
  { key: "item", type: "text", sortable: true },
  { key: "status", type: "enum", sortable: true, values: productionOrderStatuses },
  { key: "qty", type: "number", sortable: true },
  { key: "reported", type: "number", sortable: true },
  { key: "start", type: "date", sortable: true },
  { key: "due", type: "date", sortable: true },
  { key: "materials", type: "number", sortable: true },
  { key: "warehouse", type: "text", sortable: true },
];

export const plannedOrderStatuses = ["planned", "firmed", "converted"] as const;
export const plannedOrderKinds = ["purchase", "production", "subcontract"] as const;
/** Component coverage of a planned production order at the last MRP run, in severity order. */
export const plannedOrderReadiness = ["ready", "covered", "waiting", "late", "short", "no_bom"] as const;

/** planned_order_page — PX-PLN-120 (computed by MRP, export only). */
export const plannedOrderListColumns: readonly ListColumnSpec[] = [
  { key: "item", type: "text", sortable: true },
  { key: "kind", type: "enum", sortable: true, values: plannedOrderKinds },
  { key: "status", type: "enum", sortable: true, values: plannedOrderStatuses },
  { key: "qty", type: "number", sortable: true },
  { key: "start", type: "date", sortable: true },
  { key: "due", type: "date", sortable: true },
  { key: "source", type: "text", sortable: true },
  { key: "project", type: "text", sortable: true },
  { key: "readiness", type: "enum", sortable: true, values: plannedOrderReadiness },
  { key: "material", type: "date", sortable: true },
  { key: "short", type: "number", sortable: true },
];

export const inventoryDocumentStatuses = ["draft", "posted", "cancelled"] as const;

/** stock_adjustments (PostgREST) — PX-WHS-210. Reason values come from the tenant's reasons. */
export const stockAdjustmentListColumns: readonly ListColumnSpec[] = [
  { key: "number", type: "text", sortable: true },
  { key: "date", type: "date", sortable: true },
  { key: "reason", type: "enum", sortable: true },
  { key: "note", type: "text", sortable: true },
  { key: "status", type: "enum", sortable: true, values: inventoryDocumentStatuses },
];

/** stock_transfers (PostgREST) — PX-WHS-220. */
export const stockTransferListColumns: readonly ListColumnSpec[] = [
  { key: "number", type: "text", sortable: true },
  { key: "date", type: "date", sortable: true },
  { key: "note", type: "text", sortable: true },
  { key: "status", type: "enum", sortable: true, values: inventoryDocumentStatuses },
];

export const stockCountStatuses = ["draft", "in_progress", "posted", "cancelled"] as const;

/** stock_counts (PostgREST) — PX-WHS-230. */
export const stockCountListColumns: readonly ListColumnSpec[] = [
  { key: "number", type: "text", sortable: true },
  { key: "progress", type: "number", sortable: true },
  { key: "date", type: "date", sortable: true },
  { key: "note", type: "text", sortable: true },
  { key: "status", type: "enum", sortable: true, values: stockCountStatuses },
];

/** lot_page — PX-MDT-130 (computed by postings, export only). */
export const lotListColumns: readonly ListColumnSpec[] = [
  { key: "lot", type: "text", sortable: true },
  { key: "item", type: "text", sortable: true },
  { key: "expiry", type: "date", sortable: true },
  { key: "production", type: "date", sortable: true },
  { key: "supplierLot", type: "text" },
  { key: "blocked", type: "bool" },
];

/** serial_page — PX-MDT-140 (sorted by number either way; no item sort: it would sort every unit). */
export const serialListColumns: readonly ListColumnSpec[] = [
  { key: "serial", type: "text", sortable: true },
  { key: "item", type: "text" },
  { key: "status", type: "enum", values: ["in_stock", "issued"] },
  { key: "warehouse", type: "text" },
  { key: "location", type: "text" },
  { key: "lot", type: "text" },
  { key: "project", type: "text" },
];

/** stock_overview — PX-WHS-110 (computed per item: sorted by item code; filters on the item). */
export const stockListColumns: readonly ListColumnSpec[] = [
  { key: "code", type: "text", sortable: true },
  { key: "description", type: "text" },
  { key: "type", type: "enum", values: itemTypes },
  { key: "lot", type: "bool" },
];

/** locations of one warehouse (PostgREST) — PX-WHS-320 (master data). */
export const locationListColumns: readonly ListColumnSpec[] = [
  { key: "code", type: "text", sortable: true },
  { key: "description", type: "text", sortable: true },
  { key: "zone", type: "text", sortable: true },
];

/** warehouses (all of the tenant, filtered in the browser) — PX-WHS-310 (master data). */
export const warehouseListColumns: readonly ListColumnSpec[] = [
  { key: "code", type: "text", sortable: true },
  { key: "name", type: "text", sortable: true },
  { key: "company", type: "text", sortable: true },
  { key: "site", type: "text", sortable: true },
  { key: "type", type: "enum", sortable: true },
  { key: "negative", type: "bool", sortable: true },
];

/** partner_page — PX-MDT-210 / PX-SAL-310 / PX-PUR-310 (master data). */
export const partnerListColumns: readonly ListColumnSpec[] = [
  { key: "code", type: "text", sortable: true },
  { key: "name", type: "text", sortable: true },
  { key: "tax", type: "text", sortable: true },
  { key: "country", type: "text", sortable: true },
  { key: "currency", type: "text", sortable: true },
  { key: "roles", type: "enum", values: ["supplier", "customer", "both"] },
  { key: "lead", type: "number", sortable: true },
  { key: "terms", type: "text", sortable: true },
  { key: "active", type: "bool" },
];

/** purchase_receipts / shipments journals (PostgREST) — PX-PUR-210 / PX-SAL-220. */
export const journalListColumns: readonly ListColumnSpec[] = [
  { key: "number", type: "text", sortable: true },
  { key: "posted", type: "date", sortable: true },
  { key: "note", type: "text", sortable: true },
  { key: "lines", type: "number", sortable: true },
];

/** picking: open sales orders by next requested date (PostgREST) — PX-SAL-210. */
export const pickingListColumns: readonly ListColumnSpec[] = [
  { key: "date", type: "date", sortable: true },
  { key: "number", type: "text", sortable: true },
];

export const versionStatuses = ["draft", "approved", "obsolete"] as const;

/** bom_page / routing_page — PX-MFG-210 / PX-MFG-220 (master data, versions). */
export const versionListColumns: readonly ListColumnSpec[] = [
  { key: "item", type: "text", sortable: true },
  { key: "description", type: "text", sortable: true },
  { key: "version", type: "number", sortable: true },
  { key: "status", type: "enum", sortable: true, values: versionStatuses },
  { key: "from", type: "date", sortable: true },
  { key: "to", type: "date", sortable: true },
  { key: "count", type: "number", sortable: true },
];

/** Engineering BOM revisions (engineering_bom_page spec, ADR-043). */
export const engineeringBomStatuses = ["draft", "released", "obsolete"] as const;
export type EngineeringBomStatus = (typeof engineeringBomStatuses)[number];
export const engineeringBomListColumns: readonly ListColumnSpec[] = [
  { key: "item", type: "text", sortable: true },
  { key: "description", type: "text", sortable: true },
  { key: "rev", type: "number", sortable: true },
  { key: "status", type: "enum", sortable: true, values: engineeringBomStatuses },
  { key: "change", type: "text", sortable: true },
  { key: "lines", type: "number", sortable: true },
  { key: "released", type: "date", sortable: true },
];

/** Production BOM versions (pbom_page spec, ADR-043). */
export const pbomListColumns: readonly ListColumnSpec[] = [
  { key: "item", type: "text", sortable: true },
  { key: "description", type: "text", sortable: true },
  { key: "version", type: "number", sortable: true },
  { key: "status", type: "enum", sortable: true, values: versionStatuses },
  { key: "nodes", type: "number", sortable: true },
  { key: "approved", type: "date", sortable: true },
];

/** Engineering changes (engineering_change_page spec, ADR-045). */
export const ecoStatuses = ["draft", "approved", "implemented", "closed", "cancelled"] as const;
export type EcoStatus = (typeof ecoStatuses)[number];
export const ecoListColumns: readonly ListColumnSpec[] = [
  { key: "number", type: "text", sortable: true },
  { key: "title", type: "text", sortable: true },
  { key: "status", type: "enum", sortable: true, values: ecoStatuses },
  { key: "effective", type: "date", sortable: true },
  { key: "items", type: "number", sortable: true },
  { key: "created", type: "date", sortable: true },
];

export const inspectionTriggerValues = ["purchase_receipt", "production_report", "manual"] as const;
/** Inspection orders: the plan triggers plus spot checks (block_for_inspection, no plan needed). */
export const inspectionOrderTriggerValues = [...inspectionTriggerValues, "spot_check"] as const;
export const inspectionStatuses = ["open", "in_progress", "decided", "cancelled"] as const;

/** inspection_order_page — PX-QMS-110 (document). */
export const inspectionListColumns: readonly ListColumnSpec[] = [
  { key: "number", type: "text", sortable: true },
  { key: "item", type: "text", sortable: true },
  { key: "lot", type: "text", sortable: true },
  { key: "qty", type: "number", sortable: true },
  { key: "source", type: "enum", sortable: true, values: inspectionOrderTriggerValues },
  { key: "status", type: "enum", sortable: true, values: inspectionStatuses },
  { key: "created", type: "date", sortable: true },
];

export const ncrStatuses = ["open", "dispositioned", "closed", "cancelled"] as const;

/** ncr_page — PX-QMS-120 (document). */
export const ncrListColumns: readonly ListColumnSpec[] = [
  { key: "number", type: "text", sortable: true },
  { key: "item", type: "text", sortable: true },
  { key: "lot", type: "text", sortable: true },
  { key: "qty", type: "number", sortable: true },
  { key: "where", type: "text", sortable: true },
  { key: "source", type: "text", sortable: true },
  { key: "status", type: "enum", sortable: true, values: ncrStatuses },
  { key: "created", type: "date", sortable: true },
];

/** inspection_plan_page — PX-QMS-210 (master data). */
export const inspectionPlanListColumns: readonly ListColumnSpec[] = [
  { key: "item", type: "text", sortable: true },
  { key: "description", type: "text", sortable: true },
  { key: "trigger", type: "enum", sortable: true, values: inspectionTriggerValues },
  { key: "supplier", type: "text", sortable: true },
  { key: "sample", type: "number", sortable: true },
  { key: "lines", type: "number", sortable: true },
];

/** work centres (all of the company, filtered in the browser) — PX-MFG-310 (master data). */
export const workCenterListColumns: readonly ListColumnSpec[] = [
  { key: "code", type: "text", sortable: true },
  { key: "name", type: "text", sortable: true },
  { key: "kind", type: "enum", sortable: true },
  { key: "supply", type: "text", sortable: true },
  { key: "hours", type: "number", sortable: true },
];

export const projectStatuses = ["draft", "active", "completed", "closed", "cancelled"] as const;

/** project_page — PX-PRJ-110 (master data of the project module: every column sortable). */
export const projectListColumns: readonly ListColumnSpec[] = [
  { key: "code", type: "text", sortable: true },
  { key: "name", type: "text", sortable: true },
  { key: "customer", type: "text", sortable: true },
  { key: "start", type: "date", sortable: true },
  { key: "end", type: "date", sortable: true },
  { key: "hours", type: "number", sortable: true },
  { key: "planned", type: "number", sortable: true },
  { key: "status", type: "enum", sortable: true, values: projectStatuses },
];

export const planningExceptionKinds = ["expedite", "postpone", "excess", "start_in_past", "demand_in_past", "below_safety", "no_bom"] as const;

/** planning_exception_page — PX-PLN-110 (computed by MRP, export only). */
export const planningExceptionListColumns: readonly ListColumnSpec[] = [
  { key: "kind", type: "enum", sortable: true, values: planningExceptionKinds },
  { key: "item", type: "text", sortable: true },
  { key: "source", type: "text", sortable: true },
  { key: "need", type: "date", sortable: true },
  { key: "supply", type: "date", sortable: true },
  { key: "qty", type: "number", sortable: true },
];

/** project progress (computed, ≤ 500 rows, filtered in the browser) — PX-PRJ-120. Shares in percent. */
export const projectProgressListColumns: readonly ListColumnSpec[] = [
  { key: "code", type: "text", sortable: true },
  { key: "name", type: "text", sortable: true },
  { key: "hours", type: "number", sortable: true },
  { key: "milestones", type: "number", sortable: true },
  { key: "purchase", type: "number", sortable: true },
  { key: "production", type: "number", sortable: true },
  { key: "sales", type: "number", sortable: true },
  { key: "status", type: "enum", sortable: true, values: projectStatuses },
];

/** organization members (tenant_members, one page, filtered in the browser) — Settings → Users. */
export const memberListColumns: readonly ListColumnSpec[] = [
  { key: "name", type: "text", sortable: true },
  { key: "roles", type: "text", sortable: true },
  { key: "status", type: "enum", sortable: true, values: ["active", "disabled"] },
  { key: "last", type: "date", sortable: true },
];

/** invitations (≤ 200, filtered in the browser) — Settings → Users. */
export const invitationListColumns: readonly ListColumnSpec[] = [
  { key: "email", type: "text", sortable: true },
  { key: "role", type: "text", sortable: true },
  { key: "status", type: "enum", sortable: true, values: ["pending", "accepted", "revoked", "expired"] },
  { key: "created", type: "date", sortable: true },
];
